import { Collapse, Typography } from '@mui/material';
import PolicyAlert from '@acorn-portal/shared/src/components/alerts/Alert';
import { useNavigate } from 'react-router';
import { GetPaymentDetailsResponseModel, PayNowStatus } from '@acorn-portal/shared/src/services/api';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import extractBeforeDash from '@acorn-portal/shared/src/utils/extractBeforeDash';
import { DISPLAY_DATE, getContactUsUrl } from '@acorn-portal/shared/src/utils/constants';
import parser from 'html-react-parser';
import { format } from 'date-fns';
import { getPhoneNumber } from '@acorn-portal/shared/src/utils/get-phone-numbers';

interface IProps {
	payNowDetails: GetPaymentDetailsResponseModel | undefined;
}

const PayNowAlerts = ({ payNowDetails }: IProps) => {
	const navigate = useNavigate();
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	// TODO: remove product based logic and extract to parent page config
	const company = extractBeforeDash((import.meta.env.VITE_JOURNEY || '').toUpperCase());
	let message = '';
	let route = '';
	let buttonMessage = 'Make Payment';
	let href = undefined;
	let title = 'Urgent! Your policy requires payment';

	const phoneNumber = getPhoneNumber(fullPolicy.product, 'CANCELLED');
	const contactURL = getContactUsUrl(company);

	if (payNowDetails === undefined) return null;

	if (payNowDetails.missedPaymentDetails !== undefined && payNowDetails.status === PayNowStatus.DirectDebitOverdue) {
		message =
			'It appears you have missed a monthly payment for your policy. Please note that you will not be able to make changes to your policy until you make the missed payment or opt in to a split payment plan.';
		route = ROUTE_PATHS.splitPaymentPlan;
	}
	if (payNowDetails.status === PayNowStatus.ConsolidatedPaymentSchedule) {
		message = `Following your missed payment, we need you to pay the outstanding balance of ${formatCurrency(
			payNowDetails?.splitPaymentPlans[0]?.amount,
		)}. If you do not make this payment by ${format(
			new Date(payNowDetails?.splitPaymentPlans[0].dateOfPayment),
			DISPLAY_DATE,
		)}, your policy will be cancelled.`;
		route = ROUTE_PATHS.payNowSPDirectPayment;
		title = 'Urgent! Payment Failed';
	}

	if (payNowDetails.status === PayNowStatus.SplitPaymentOverdue) {
		message =
			"We have been unable to collect one of your weekly payments. To pay the outstanding balance, please click 'Make Payment'. If you do not make this payment by the date shown in the 'Payments' section, your policy will be cancelled";
		route = ROUTE_PATHS.paymentPlanOverdue;
	}

	if (payNowDetails.status === PayNowStatus.CancelledWithDebt) {
		message =
			company === 'MOTORCADE'
				? 'Your policy has been cancelled, please click the button below to contact us via webchat.'
				: `Your policy has been cancelled, please click the button below to contact us via webchat or call us on <a href="tel:${phoneNumber}">${phoneNumber}</a> if you need to speak to us.`;

		href = contactURL;
		buttonMessage = 'Contact Us';
		title = 'Your Policy Is Cancelled';
	}

	if (message === '') return null;

	return (
		<Collapse in>
			<PolicyAlert
				type='error'
				title={title}
				onActionClick={route ? () => navigate(route) : undefined}
				actionText={buttonMessage}
				href={href}
			>
				<Typography>{parser(message)}</Typography>
			</PolicyAlert>
		</Collapse>
	);
};

export default PayNowAlerts;
